import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getChatJustifyData as getAnonymousChatJustifyData,
  postChatJustifyDrill,
} from 'src/api/backstage/anonymous';
import {
  getChatJustifyDataAPI,
  postChatJustifyDrillAPI,
} from 'src/api/conversation/chat';
import { createAppAsyncThunk } from 'src/hooks/hooks';
import { ChatJustify } from 'src/types/conversation';

export interface ChatJustifyState {
  loadingMap: Record<string, boolean>;
  expandedMap: Record<string, boolean>;
  dataMap: Record<string, ChatJustify.DataItem[]>;
  drillDataMap: Record<string, ChatJustify.DrillData | null>;
  drillLoadingMap: Record<string, boolean>;
}

const initialState: ChatJustifyState = {
  loadingMap: {},
  expandedMap: {},
  dataMap: {},
  drillDataMap: {},
  drillLoadingMap: {},
};

/** 获取佐证数据 */
export const getChatJustifyData = createAppAsyncThunk(
  'chatJustify/getChatJustifyData',
  async (params: ChatJustify.ListQuery, { getState }) => {
    const {
      conversation: { isAnonymous, tenant_id },
    } = getState();
    try {
      let res;
      if (isAnonymous) {
        res = await getAnonymousChatJustifyData({ ...params, tenant_id });
      } else {
        res = await getChatJustifyDataAPI(params);
      }
      const {
        json: { result },
      } = res;
      return result || [];
    } catch (error) {
      console.error(error);
      return [];
    }
  },
);

/** 获取下钻详情 */
export const getDrillData = createAppAsyncThunk(
  'chatJustify/getDrillData',
  async (params: ChatJustify.DrillQuery, { getState }) => {
    try {
      const {
        conversation: { isAnonymous, tenant_id },
      } = getState();
      const res = isAnonymous
        ? await postChatJustifyDrill({ ...params, tenant_id })
        : await postChatJustifyDrillAPI(params);
      const {
        json: { result },
      } = res;
      return result || null;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
);

/** 生成下钻数据map的key */
export const generateDrillMapKey = (
  round_id: string,
  step: number,
  rowData: ChatJustify.DataItem['data'][number],
  colname: string,
) => {
  return `${round_id}_${step}_${colname}_${JSON.stringify(rowData)}`;
};

/** 佐证系统 */
const chatJustifySlice = createSlice({
  name: 'chatJustify',
  initialState,
  reducers: {
    setLoading(
      state,
      action: PayloadAction<{ round_id: string; loading: boolean }>,
    ) {
      state.loadingMap[action.payload.round_id] = action.payload.loading;
    },
    setExpanded(
      state,
      action: PayloadAction<{ round_id: string; expanded: boolean }>,
    ) {
      state.expandedMap[action.payload.round_id] = action.payload.expanded;
    },
    setData(
      state,
      action: PayloadAction<{ round_id: string; data: ChatJustify.DataItem[] }>,
    ) {
      state.dataMap[action.payload.round_id] = action.payload.data;
    },
    removeData(state, action: PayloadAction<{ round_id: string }>) {
      const { round_id } = action.payload;
      delete state.dataMap[round_id];
      delete state.expandedMap[round_id];
      delete state.loadingMap[round_id];
      Object.keys(state.drillDataMap).forEach(key => {
        if (key.includes(round_id)) {
          delete state.drillDataMap[key];
          delete state.drillLoadingMap[key];
        }
      });
    },
    clearData() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder.addCase(getChatJustifyData.fulfilled, (state, action) => {
      const { round_id } = action.meta.arg;
      state.expandedMap[round_id] = true;
      state.loadingMap[round_id] = false;
      state.dataMap[round_id] = action.payload;
    });
    builder.addCase(getChatJustifyData.pending, (state, action) => {
      state.loadingMap[action.meta.arg.round_id] = true;
    });
    builder.addCase(getDrillData.fulfilled, (state, action) => {
      const { round_id, step, row_data, colname } = action.meta.arg;
      const key = generateDrillMapKey(round_id, step, row_data, colname);
      state.drillDataMap[key] = action.payload;
      state.drillLoadingMap[key] = false;
    });
    builder.addCase(getDrillData.pending, (state, action) => {
      const { round_id, step, row_data, colname } = action.meta.arg;
      const key = generateDrillMapKey(round_id, step, row_data, colname);
      state.drillLoadingMap[key] = true;
    });
  },
});

export const { setLoading, setExpanded, setData, removeData } =
  chatJustifySlice.actions;

export default chatJustifySlice.reducer;
